import { COMPANY, DOC_TYPES, calcTotals } from './documentsApi';

const LOGO_URL = `${window.location.origin}/LOGO_FYM-01.png`;
const SIGNATURE_PP = `${window.location.origin}/sig-pp.png`;
const esc  = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
const fmt  = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function thDateShort(str) {
  if (!str) return '-';
  try {
    const d  = new Date(str);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    // Thai legal/tax documents use the Buddhist Era year (พ.ศ. = ค.ศ. + 543).
    return `${dd}/${mm}/${d.getFullYear() + 543}`;
  } catch { return str; }
}

// ─── Thai Baht → words ────────────────────────────────────────────────────────
function bahtText(amount) {
  const abs = Math.abs(Number(amount) || 0);
  if (abs === 0) return 'ศูนย์บาทถ้วน';
  const UNITS = ['', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];

  function below1M(n, forceEd = false) {
    if (n === 0) return '';
    const d = String(n).padStart(6, '0').split('').map(Number);
    // d[0]=แสน d[1]=หมื่น d[2]=พัน d[3]=ร้อย d[4]=สิบ d[5]=หน่วย
    const place = ['แสน', 'หมื่น', 'พัน', 'ร้อย', 'สิบ', ''];
    // A '1' in the units place is read "เอ็ด" whenever the number has any
    // higher-order value — within this chunk (n >= 11, e.g. 101 → หนึ่งร้อยเอ็ด)
    // or in a higher chunk (forceEd, e.g. 1,000,001 → หนึ่งล้านเอ็ด).
    const unitsEd = n >= 11 || forceEd;
    let r = '';
    for (let i = 0; i < 6; i++) {
      const v = d[i];
      if (!v) continue;
      if (i === 4) { r += v === 1 ? 'สิบ' : v === 2 ? 'ยี่สิบ' : UNITS[v] + 'สิบ'; }
      else if (i === 5) { r += (v === 1 && unitsEd) ? 'เอ็ด' : UNITS[v]; }
      else r += UNITS[v] + place[i];
    }
    return r;
  }

  function toWords(n) {
    const m  = Math.floor(n / 1_000_000);
    const lo = n % 1_000_000;
    return (m ? below1M(m) + 'ล้าน' : '') + below1M(lo, m > 0);
  }

  const intPart = Math.floor(abs);
  const dec     = Math.round((abs - intPart) * 100);
  return toWords(intPart) + 'บาท' + (dec ? toWords(dec) + 'สตางค์' : 'ถ้วน');
}

// ─── BASE CSS ─────────────────────────────────────────────────────────────────
const BASE_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;500;600;700;800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{
    font-family:'Sarabun',Tahoma,sans-serif;
    color:#111;background:#fff;
    padding:24px 32px;font-size:13px;line-height:1.65;
    max-width:210mm;margin:0 auto;
  }

  /* ── HEADER GRID ─────────────────────────────────────────────────── */
  .doc-header{display:grid;grid-template-columns:1fr 265px;gap:24px;margin-bottom:0}

  .co-block{display:flex;gap:12px;align-items:flex-start}
  .co-logo{width:72px;height:72px;object-fit:contain;flex-shrink:0}
  .co-info{font-size:11px;color:#333;line-height:1.9}
  .co-name-th{font-size:14.5px;font-weight:800;color:#000}

  .meta-right{text-align:right}
  .doc-title{font-size:22px;font-weight:800;color:#000;line-height:1.5;white-space:nowrap}
  .doc-title-en{font-size:11px;color:#888;margin-top:2px}
  .meta-rule{border:none;border-top:2px solid #000;margin:8px 0 6px}
  .meta-tbl{width:100%;border-collapse:collapse;font-size:11.5px}
  .meta-tbl td{padding:2.5px 0}
  .meta-tbl .ml{color:#444;text-align:left;padding-right:8px;white-space:nowrap}
  .meta-tbl .mv{font-weight:700;color:#000;text-align:right}
  .meta-thin{border:none;border-top:1px solid #ccc;margin:5px 0}

  /* ── HEADER RULE + CUSTOMER ── */
  .header-rule{border:none;border-top:1.5px solid #000;margin:12px 0}
  .cust-label{font-size:10px;font-weight:700;color:#777;letter-spacing:.6px;text-transform:uppercase;margin-bottom:2px}
  .cust-name{font-size:13px;font-weight:700;color:#000}
  .cust-sub{font-size:11px;color:#444;line-height:1.85;margin-top:1px}

  /* ── TABLE ── */
  .items-table{width:100%;border-collapse:collapse;margin:6px 0 0}
  .items-table thead th{
    background:#333;color:#fff;
    padding:7px 8px;font-size:11px;font-weight:700;
    text-align:left;border:none;
  }
  .items-table thead th.r{text-align:right}
  .items-table thead th.c{text-align:center}
  .items-table tbody tr{border-bottom:1px solid #eee}
  .items-table tbody tr:last-child{border-bottom:none}
  .items-table tbody td{
    padding:7px 8px;font-size:12px;vertical-align:top;
    border-left:none;border-right:none;
  }
  .items-table tbody td.r{text-align:right;font-variant-numeric:tabular-nums}
  .items-table tbody td.c{text-align:center}
  .items-table tfoot tr{border-top:1.5px solid #333}
  .items-table tfoot td{padding:5px 8px;font-size:11.5px;font-weight:700;background:#f7f7f7}
  .items-table tfoot td.r{text-align:right}
  .unit-sub{font-size:10px;color:#888}

  /* ── SUMMARY (FlowAccount no-box style) ── */
  .summary-outer{display:flex;justify-content:flex-end;margin-top:16px}
  .summary-col{width:345px;font-size:12px}
  .srow{display:flex;justify-content:space-between;align-items:baseline;padding:2.5px 0}
  .srow .sl{color:#333}
  .srow .sv{font-variant-numeric:tabular-nums;color:#000;font-weight:500;min-width:128px;text-align:right}
  .srow.sub .sl,.srow.sub .sv{color:#555;font-size:11.5px}
  .srow.total-row .sl{font-size:13.5px;font-weight:800}
  .srow.total-row .sv{font-size:13.5px;font-weight:800}
  .srow.pay-row .sl{font-size:15px;font-weight:800}
  .srow.pay-row .sv{font-size:15px;font-weight:800}
  .shr{border:none;border-top:1px solid #ccc;margin:4px 0}
  .shr-bold{border:none;border-top:1.5px solid #000;margin:5px 0}

  /* ── BAHT TEXT ── */
  .baht-text{margin-top:10px;font-size:12px;color:#555;font-style:italic}
  .baht-text b{font-style:normal;font-weight:700;color:#000}

  /* ── NOTES ── */
  .note-section{margin-top:14px}
  .note-lbl{font-size:11.5px;font-weight:700;color:#000;margin-bottom:3px}
  .note-body{font-size:11.5px;color:#333;line-height:1.9;white-space:pre-line}

  /* ── STAMP ── */
  .stamp-wrap{display:flex;justify-content:flex-end;margin-top:10px}
  .stamp{
    display:inline-block;padding:5px 18px;
    border:3px solid #000;border-radius:4px;
    font-weight:800;font-size:15px;letter-spacing:2px;
    transform:rotate(-8deg);color:#000;
  }

  /* ── PAGE 2: SIGNATURE ── */
  .page-break{page-break-before:always;padding-top:24px}
  .sig-space{height:140px}
  .sig-section{display:grid;grid-template-columns:1fr 1fr;gap:48px;margin-top:20px}
  .sig-box{text-align:center;position:relative}
  .sig-behalf{font-size:12px;font-weight:700;color:#000;margin-bottom:6px}
  .sig-company{font-size:11px;color:#555;margin-bottom:8px}
  .sig-img{width:270px;height:120px;object-fit:contain;display:block;margin:0 auto}
  .sig-placeholder{height:120px;display:block}
  .sig-line{border-top:1px solid #000;margin:0 16px;padding-top:6px}
  .sig-role{font-size:12px;font-weight:600;color:#000}
  .sig-date{font-size:11px;color:#555;margin-top:4px}

  /* ── WT SPECIFIC ── */
  .wt-parties{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:10px 0}
  .wt-party{border:1px solid #ccc;border-radius:3px;padding:10px 12px}
  .wt-party .lbl{font-size:10px;font-weight:700;color:#777;text-transform:uppercase;letter-spacing:.5px;margin-bottom:3px}
  .wt-party .nm{font-size:13px;font-weight:700;color:#000}
  .wt-party .sb{font-size:11px;color:#444;line-height:1.8;margin-top:2px}
  .wt-type-list{display:flex;flex-wrap:wrap;gap:8px 28px;padding:10px 0;margin-bottom:10px}
  .wt-type-list label{display:flex;align-items:center;gap:6px;font-size:12px}
  .wt-table{width:100%;border-collapse:collapse;margin:10px 0}
  .wt-table th,.wt-table td{border:1px solid #ccc;padding:7px 9px;font-size:12px}
  .wt-table thead th{background:#333;color:#fff;font-weight:700}
  .wt-table .r{text-align:right}
  .wt-table tfoot td{background:#f5f5f5;font-weight:700}

  /* ── BANK ACCOUNTS ── */
  .bank-section{margin-top:14px;padding:9px 12px;border:1px solid #ddd;border-radius:4px;background:#fafafa}
  .bank-lbl{font-size:10px;font-weight:700;color:#666;text-transform:uppercase;letter-spacing:.5px;margin-bottom:5px}
  .bank-row{font-size:11.5px;color:#333;line-height:1.9}
  .bank-name{font-weight:700;color:#000}
  .bank-acnum{font-weight:700;color:#1a56db;letter-spacing:.5px}
  .bank-actype,.bank-branch,.bank-acname{color:#555}

  /* ── PRINT BUTTON ── */
  .print-btn{
    display:block;margin:24px 0 0 auto;
    padding:8px 24px;background:#000;color:#fff;border:none;
    border-radius:5px;font-size:13px;font-weight:700;cursor:pointer;
    font-family:inherit;
  }
  @media print{
    .print-btn{display:none}
    body{padding:0}
    .items-table thead th,.wt-table thead th,
    .items-table tfoot td{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  }
  @page{margin:14mm;size:A4}
`;

// ─── Shared: header (FlowAccount 2-column) ────────────────────────────────────
function pageHeader(doc, compact = false) {
  const type   = DOC_TYPES[doc.doc_type] || {};
  const c      = doc.customer || {};
  const credit = doc.credit_days != null && doc.credit_days !== '' ? `${doc.credit_days} วัน` : '-';
  const seller = doc.seller_name || '-';

  const leftCol = compact ? '' : `
      <div class="cust-label">ลูกค้า</div>
      <div class="cust-name">${esc(c.name || '-')}</div>
      <div class="cust-sub">
        ${c.tax_id   ? `เลขประจำตัวผู้เสียภาษี: ${esc(c.tax_id)}${c.branch ? ` (${/^0*$/.test(String(c.branch).replace(/\D/g,'')) ? 'สำนักงานใหญ่' : (String(c.branch).includes('สาขา') ? esc(c.branch) : 'สาขา ' + esc(c.branch))})` : ''}<br>` : ''}
        ${c.address  ? esc(c.address) + '<br>' : ''}
        ${c.phone    ? `โทร. ${esc(c.phone)}` : ''}
        ${c.email    ? ` | ${esc(c.email)}` : ''}
      </div>`;

  return `
  <div class="doc-header">
    <div>
      <div class="co-block">
        <img src="${LOGO_URL}" class="co-logo" alt="Logo" />
        <div class="co-info">
          <div class="co-name-th">${esc(COMPANY.name)}</div>
          ${esc(COMPANY.nameEn)}<br>
          เลขประจำตัวผู้เสียภาษี: ${esc(COMPANY.tax_id)}<br>
          ${esc(COMPANY.address)}<br>
          ${COMPANY.phone ? `โทร. ${esc(COMPANY.phone)}` : ''}
          ${COMPANY.email ? ` | ${esc(COMPANY.email)}` : ''}
        </div>
      </div>
      ${leftCol}
    </div>
    <div class="meta-right">
      <div class="doc-title">${esc(type.label || doc.doc_type)}</div>
      <div class="doc-title-en">${esc(type.labelEn || '')}</div>
      <hr class="meta-rule" />
      <table class="meta-tbl">
        <tr><td class="ml">เลขที่</td><td class="mv">${esc(doc.doc_number || '-')}</td></tr>
        <tr><td class="ml">วันที่</td><td class="mv">${thDateShort(doc.issue_date)}</td></tr>
        <tr><td class="ml">เครดิต</td><td class="mv">${esc(credit)}</td></tr>
        <tr><td class="ml">ผู้ขาย</td><td class="mv">${esc(seller)}</td></tr>
      </table>
      ${doc.job_name ? `<hr class="meta-thin" /><table class="meta-tbl"><tr><td class="ml">ชื่องาน</td><td class="mv">${esc(doc.job_name)}</td></tr></table>` : ''}
    </div>
  </div>
  <hr class="header-rule" />`;
}

// ─── Items table ──────────────────────────────────────────────────────────────
// vatMode: 'vat' (default, show VAT column) | 'none' (hide VAT column)
function itemsTable(items = [], vatMode = 'vat') {
  const showVat = vatMode !== 'none';
  const colSpan = showVat ? 7 : 6;

  const rows = items.map((it, i) => {
    const qty       = Number(it.qty) || 0;
    const unitPrice = Number(it.unit_price) || 0;
    const lineGross = qty * unitPrice;
    const discVal   = Number(it.discount_value) || 0;
    const disc      = it.discount_type === 'percent'
      ? Math.min(lineGross * discVal / 100, lineGross)
      : Math.min(discVal, lineGross);
    const lineNet   = lineGross - disc;
    const vatType   = it.vat_type || 'vat7';
    const discDisp  = disc > 0
      ? (it.discount_type === 'percent' ? `${discVal} %` : fmt(disc))
      : '-';
    const vatDisp   = vatType === 'vat7' ? '7 %' : vatType === 'zero' ? '0 %' : 'ยกเว้น';
    const unitDisp  = it.unit ? `<span class="unit-sub">${esc(it.unit)}</span>` : '';

    const detailDisp = it.detail
      ? `<div style="font-size:10.5px;color:#666;font-style:italic;margin-top:2px;line-height:1.5">${esc(it.detail).replace(/\n/g,'<br>')}</div>`
      : '';

    return `<tr>
      <td class="c">${i + 1}</td>
      <td>${esc(it.description)}${detailDisp}</td>
      <td class="c">${fmtN(qty)} ${unitDisp}</td>
      <td class="r">${fmt(unitPrice)}</td>
      <td class="r">${discDisp}</td>
      ${showVat ? `<td class="c">${vatDisp}</td>` : ''}
      <td class="r">${fmt(lineNet)}</td>
    </tr>`;
  }).join('');

  return `
  <table class="items-table">
    <thead><tr>
      <th class="c" style="width:28px">#</th>
      <th>รายละเอียด</th>
      <th class="c" style="width:72px">จำนวน</th>
      <th class="r" style="width:110px">ราคาต่อหน่วย</th>
      <th class="r" style="width:76px">ส่วนลด</th>
      ${showVat ? '<th class="c" style="width:58px">ภาษี</th>' : ''}
      <th class="r" style="width:110px">มูลค่า</th>
    </tr></thead>
    <tbody>${rows || `<tr><td colspan="${colSpan}" style="text-align:center;color:#aaa;padding:18px">ไม่มีรายการ</td></tr>`}</tbody>
  </table>`;
}

// ─── Summary (FlowAccount exact format) ───────────────────────────────────────
function totalsSection(doc) {
  const vatMode = doc.vat_mode || 'vat';
  const noVat   = vatMode === 'none';

  // When noVat, treat all items as exempt for calc (force wht base = sum of nets)
  const calcItems = noVat
    ? (doc.items || []).map(it => ({ ...it, vat_type: 'exempt' }))
    : (doc.items || []);

  const t   = calcTotals({ items: calcItems, wht_rate: doc.wht_rate || 0, price_mode: doc.price_mode || 'exclusive' });
  const netAD      = t.subtotal_vatable + t.subtotal_exempt;
  const grandTotal = t.total + t.wht_amount;
  const payable    = t.total;

  const vatRows = noVat ? '' : `
      <div class="srow sub">
        <span class="sl">มูลค่าที่ไม่มี/ยกเว้นภาษี</span>
        <span class="sv">${fmt(t.subtotal_exempt)} บาท</span>
      </div>
      <div class="srow sub">
        <span class="sl">มูลค่าที่คำนวณภาษี</span>
        <span class="sv">${fmt(t.subtotal_vatable_excl)} บาท</span>
      </div>
      <div class="srow sub">
        <span class="sl">ภาษีมูลค่าเพิ่ม 7%</span>
        <span class="sv">${fmt(t.vat_amount)} บาท</span>
      </div>`;

  return `
  <div class="summary-outer">
    <div class="summary-col">
      <div class="srow">
        <span class="sl">รวมเป็นเงิน</span>
        <span class="sv">${fmt(t.subtotal_before_discount)} บาท</span>
      </div>
      <div class="srow">
        <span class="sl">ส่วนลด</span>
        <span class="sv">${fmt(t.total_discount)} บาท</span>
      </div>
      <hr class="shr" />
      <div class="srow">
        <span class="sl" style="font-weight:600">จำนวนเงินหลังหักส่วนลด</span>
        <span class="sv" style="font-weight:700">${fmt(netAD)} บาท</span>
      </div>
      ${vatRows}
      <hr class="shr-bold" />
      <div class="srow total-row">
        <span class="sl">จำนวนเงินรวมทั้งสิ้น</span>
        <span class="sv">${fmt(grandTotal)} บาท</span>
      </div>
      <div class="srow">
        <span class="sl">หักภาษี ณ ที่จ่ายทั้งสิ้น</span>
        <span class="sv">${fmt(t.wht_amount)} บาท</span>
      </div>
      <hr class="shr-bold" />
      <div class="srow pay-row">
        <span class="sl">ยอดชำระ</span>
        <span class="sv">${fmt(payable)} บาท</span>
      </div>
    </div>
  </div>
  <div class="baht-text">(<b>${bahtText(payable)}</b>)</div>`;
}

// ─── Signature section ────────────────────────────────────────────────────────
function sigSection(doc, buyerRole) {
  const cName = doc.customer?.name || 'ลูกค้า';
  return `
  <div class="sig-section">
    <div class="sig-box">
      <div class="sig-behalf">${esc(buyerRole)}</div>
      <div class="sig-company">ในนาม ${esc(cName)}</div>
      <span class="sig-placeholder"></span>
      <div class="sig-line"></div>
      <div class="sig-role">${esc(buyerRole)}</div>
      <div class="sig-date">วันที่ ......./......./.........</div>
    </div>
    <div class="sig-box">
      <div class="sig-behalf">ผู้อนุมัติ</div>
      <div class="sig-company">ในนาม ${esc(COMPANY.name)}</div>
      <img src="${SIGNATURE_PP}" class="sig-img" alt="signature" />
      <div class="sig-line"></div>
      <div class="sig-role">ปราณปัทม์ ปฏิพัตร์</div>
      <div class="sig-date">วันที่ ......./......./.........</div>
    </div>
  </div>`;
}

// ─── Notes ────────────────────────────────────────────────────────────────────
function notesBlock(doc) {
  return doc.notes
    ? `<div class="note-section">
        <div class="note-lbl">หมายเหตุ</div>
        <div class="note-body">${esc(doc.notes)}</div>
       </div>`
    : '';
}

// ─── Bank accounts ────────────────────────────────────────────────────────────
function bankBlock(accounts) {
  const list = accounts || COMPANY.bank_accounts;
  if (!list || list.length === 0) return '';
  const rows = list.map(a => `
    <div class="bank-row">
      <span class="bank-name">${esc(a.bank)}</span>
      <span class="bank-branch"> สาขา${esc(a.branch)}</span>
      &nbsp;|&nbsp;
      <span class="bank-actype">${esc(a.account_type)}</span>
      &nbsp;
      <span class="bank-acnum">${esc(a.account_number)}</span>
      &nbsp;|&nbsp;
      <span class="bank-acname">ชื่อบัญชี ${esc(a.account_name)}</span>
    </div>`).join('');
  return `
  <div class="bank-section">
    <div class="bank-lbl">ชำระเงินผ่านบัญชีธนาคาร</div>
    ${rows}
  </div>`;
}

// ─── QT ──────────────────────────────────────────────────────────────────────
function templateQT(doc, bankAccounts) {
  return `<!doctype html><html lang="th"><head><meta charset="utf-8">
  <title>${esc(doc.doc_number)}</title><style>${BASE_CSS}</style></head><body>
  ${pageHeader(doc)}
  ${itemsTable(doc.items, doc.vat_mode)}
  ${totalsSection(doc)}
  ${bankBlock(bankAccounts)}
  ${notesBlock(doc)}
  <div class="note-section" style="margin-top:8px">
    <div class="note-body" style="font-size:10.5px;color:#888">
      * ใบเสนอราคานี้มีผลภายใน 30 วัน นับจากวันที่ออกเอกสาร
    </div>
  </div>
  <div class="page-break">
    ${pageHeader(doc, true)}
    <div class="sig-space"></div>
    ${sigSection(doc, 'ผู้สั่งซื้อสินค้า')}
  </div>
  <button class="print-btn" onclick="window.print()">พิมพ์ / บันทึก PDF</button>
  </body></html>`;
}

// ─── IV ──────────────────────────────────────────────────────────────────────
function templateIV(doc, bankAccounts) {
  return `<!doctype html><html lang="th"><head><meta charset="utf-8">
  <title>${esc(doc.doc_number)}</title><style>${BASE_CSS}</style></head><body>
  ${pageHeader(doc)}
  ${itemsTable(doc.items, doc.vat_mode)}
  ${totalsSection(doc)}
  ${bankBlock(bankAccounts)}
  ${notesBlock(doc)}
  <div class="page-break">
    ${pageHeader(doc, true)}
    <div class="sig-space"></div>
    ${sigSection(doc, 'ผู้รับเอกสาร')}
  </div>
  <button class="print-btn" onclick="window.print()">พิมพ์ / บันทึก PDF</button>
  </body></html>`;
}

// ─── RC ──────────────────────────────────────────────────────────────────────
function templateRC(doc, bankAccounts) {
  return `<!doctype html><html lang="th"><head><meta charset="utf-8">
  <title>${esc(doc.doc_number)}</title><style>${BASE_CSS}</style></head><body>
  ${pageHeader(doc)}
  ${itemsTable(doc.items, doc.vat_mode)}
  ${totalsSection(doc)}
  <div class="stamp-wrap"><span class="stamp">ชำระเงินแล้ว</span></div>
  ${bankBlock(bankAccounts)}
  ${notesBlock(doc)}
  <div class="page-break">
    ${pageHeader(doc, true)}
    <div class="sig-space"></div>
    ${sigSection(doc, 'ผู้ชำระเงิน')}
  </div>
  <button class="print-btn" onclick="window.print()">พิมพ์ / บันทึก PDF</button>
  </body></html>`;
}

// ─── TI ──────────────────────────────────────────────────────────────────────
function templateTI(doc, bankAccounts) {
  return `<!doctype html><html lang="th"><head><meta charset="utf-8">
  <title>${esc(doc.doc_number)}</title><style>${BASE_CSS}</style></head><body>
  ${pageHeader(doc)}
  ${itemsTable(doc.items, doc.vat_mode)}
  ${totalsSection(doc)}
  <div class="note-section" style="margin-top:10px">
    <div class="note-body" style="font-size:10.5px;color:#555">
      ผู้ประกอบการจดทะเบียนภาษีมูลค่าเพิ่ม · เลขที่ผู้เสียภาษี ${esc(COMPANY.tax_id)}
    </div>
  </div>
  ${bankBlock(bankAccounts)}
  ${notesBlock(doc)}
  <div class="page-break">
    ${pageHeader(doc, true)}
    <div class="sig-space"></div>
    ${sigSection(doc, 'ผู้รับเอกสาร')}
  </div>
  <button class="print-btn" onclick="window.print()">พิมพ์ / บันทึก PDF</button>
  </body></html>`;
}

// ─── WT ──────────────────────────────────────────────────────────────────────
function templateWT(doc) {
  const c       = doc.customer || {};
  const wht     = doc.wht_rate || 0;
  const t       = calcTotals({ items: doc.items || [], wht_rate: wht, price_mode: doc.price_mode || 'exclusive' });

  const checks  = [
    { rates: [1],    label: '1. เงินเดือน ค่าจ้าง ฯลฯ' },
    { rates: [3, 5], label: '2. ค่าจ้างทำของ / ค่าบริการ' },
    { rates: [2],    label: '3. ค่าเช่า' },
    { rates: [10],   label: '4. ค่าวิชาชีพอิสระ' },
    { rates: [],     label: '5. อื่นๆ' },
  ].map(({ rates, label }) => {
    const checked = rates.includes(wht);
    return `<label><input type="checkbox" ${checked ? 'checked' : ''} disabled> ${label}</label>`;
  }).join('');

  const itemRows = (doc.items || []).map(it => {
    const gross = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
    const disc  = it.discount_type === 'percent'
      ? gross * (Number(it.discount_value) || 0) / 100
      : Number(it.discount_value) || 0;
    const net   = gross - Math.min(disc, gross);
    const wtAmt = Math.round(net * (wht / 100) * 100) / 100;
    return `<tr><td>${esc(it.description)}</td><td class="r">${fmt(net)}</td><td class="r">${wht}%</td><td class="r">${fmt(wtAmt)}</td></tr>`;
  }).join('') || `<tr><td colspan="4" style="text-align:center;color:#aaa;padding:16px">ไม่มีรายการ</td></tr>`;

  return `<!doctype html><html lang="th"><head><meta charset="utf-8">
  <title>${esc(doc.doc_number)}</title><style>${BASE_CSS}</style></head><body>
  ${pageHeader(doc, true)}
  <div style="font-size:12px;margin-bottom:10px">
    <strong>วันที่จ่ายเงิน:</strong> ${thDateShort(doc.issue_date)}
    &nbsp;&nbsp;<strong>เลขที่:</strong> ${esc(doc.doc_number)}
  </div>
  <div class="wt-parties">
    <div class="wt-party">
      <div class="lbl">ผู้มีหน้าที่หักภาษี ณ ที่จ่าย</div>
      <div class="nm">${esc(COMPANY.name)}</div>
      <div class="sb">เลขประจำตัวผู้เสียภาษี: ${esc(COMPANY.tax_id)}<br>${esc(COMPANY.address)}</div>
    </div>
    <div class="wt-party">
      <div class="lbl">ผู้ถูกหักภาษี ณ ที่จ่าย</div>
      <div class="nm">${esc(c.name || '-')}</div>
      <div class="sb">${c.tax_id ? `เลขประจำตัวผู้เสียภาษี: ${esc(c.tax_id)}<br>` : ''}${esc(c.address || '')}</div>
    </div>
  </div>
  <div class="wt-type-list">${checks}</div>
  <table class="wt-table">
    <thead><tr>
      <th>รายการ</th>
      <th class="r" style="width:130px">ยอดเงินที่จ่าย</th>
      <th class="r" style="width:80px">อัตรา</th>
      <th class="r" style="width:120px">ภาษีที่หัก</th>
    </tr></thead>
    <tbody>${itemRows}</tbody>
    <tfoot><tr>
      <td style="text-align:right">รวม</td>
      <td class="r">${fmt(t.subtotal)}</td>
      <td class="r">${wht}%</td>
      <td class="r">${fmt(t.wht_amount)}</td>
    </tr></tfoot>
  </table>
  <div style="font-size:11.5px;color:#333;line-height:2;margin-top:6px">
    ☐ หัก ณ ที่จ่ายครั้งเดียว &nbsp;&nbsp; ☐ หักทุกครั้งที่จ่าย<br>
    รวมเงินภาษีที่หักและนำส่งในครั้งนี้ทั้งสิ้น <strong>${fmt(t.wht_amount)} บาท</strong>
    <br>(<b>${bahtText(t.wht_amount)}</b>)
  </div>
  ${notesBlock(doc)}
  <div class="sig-section" style="margin-top:48px">
    <div class="sig-box">
      <div class="sig-line"></div>
      <div class="sig-role">ผู้จ่ายเงิน</div>
      <div class="sig-date">วันที่ ......./......./.........</div>
    </div>
    <div class="sig-box">
      <div class="sig-line"></div>
      <div class="sig-role">ผู้รับเงิน</div>
      <div class="sig-date">วันที่ ......./......./.........</div>
    </div>
  </div>
  <button class="print-btn" onclick="window.print()">พิมพ์ / บันทึก PDF</button>
  </body></html>`;
}

// ─── WC — หนังสือรับรองการหักภาษี ณ ที่จ่าย ตามมาตรา 50 ทวิ ─────────────────
const INCOME_SECTIONS = [
  { key: '40_1',  label: '(1) มาตรา 40(1)', desc: 'เงินเดือน ค่าจ้าง โบนัส เบี้ยเลี้ยง ฯลฯ' },
  { key: '40_2',  label: '(2) มาตรา 40(2)', desc: 'ค่าธรรมเนียม ค่านายหน้า ค่าส่วนลด ฯลฯ' },
  { key: '40_3',  label: '(3) มาตรา 40(3)', desc: 'ค่าแห่งกู๊ดวิลล์ ค่าลิขสิทธิ์ ค่าสิทธิ ฯลฯ' },
  { key: '40_4a', label: '(4ก) มาตรา 40(4)(ก)', desc: 'ดอกเบี้ย ฯลฯ' },
  { key: '40_4b', label: '(4ข) มาตรา 40(4)(ข)', desc: 'เงินปันผล ส่วนแบ่งกำไร ฯลฯ' },
  { key: '40_5',  label: '(5) มาตรา 40(5)', desc: 'ค่าเช่าทรัพย์สิน ฯลฯ' },
  { key: '40_6',  label: '(6) มาตรา 40(6)', desc: 'วิชาชีพอิสระ ฯลฯ' },
  { key: '40_7',  label: '(7) มาตรา 40(7)', desc: 'รับเหมา ฯลฯ' },
  { key: '40_8',  label: '(8) มาตรา 40(8)', desc: 'ค่าบริการ / อื่นๆ ฯลฯ' },
];
export { INCOME_SECTIONS };

const WC_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;500;600;700;800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Sarabun',Tahoma,sans-serif;color:#000;background:#fff;padding:20px 28px;font-size:12.5px;line-height:1.7;max-width:210mm;margin:0 auto}
  .wc-title{text-align:center;margin-bottom:14px}
  .wc-title .th{font-size:16px;font-weight:800;color:#000;line-height:1.4}
  .wc-title .en{font-size:11px;color:#555;margin-top:2px}
  .wc-title .sub{font-size:12px;color:#000;margin-top:4px}

  .parties{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:12px}
  .party-box{border:1px solid #ccc;border-radius:3px;padding:9px 12px}
  .party-box .role{font-size:10px;font-weight:700;color:#777;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px}
  .party-box .name{font-size:13px;font-weight:700;color:#000}
  .party-box .sub{font-size:11px;color:#444;line-height:1.8;margin-top:1px}

  .section-lbl{font-size:11.5px;font-weight:700;color:#000;margin:10px 0 5px}
  .income-grid{display:grid;grid-template-columns:1fr 1fr;gap:3px 16px;margin-bottom:10px;font-size:11.5px}
  .income-item{display:flex;align-items:flex-start;gap:7px}
  .income-item input[type=checkbox]{margin-top:3px;flex-shrink:0;accent-color:#000}
  .income-item .item-label{line-height:1.5}
  .income-item .item-desc{font-size:10.5px;color:#555}

  .wc-table{width:100%;border-collapse:collapse;margin:8px 0}
  .wc-table th{background:#333;color:#fff;padding:6px 8px;font-size:11px;font-weight:700;text-align:left;border:1px solid #333}
  .wc-table th.r{text-align:right}
  .wc-table th.c{text-align:center}
  .wc-table td{border:1px solid #ccc;padding:6px 8px;font-size:11.5px;vertical-align:top}
  .wc-table td.r{text-align:right;font-variant-numeric:tabular-nums}
  .wc-table tfoot td{background:#f5f5f5;font-weight:700;border:1px solid #aaa}
  .wc-table tfoot td.r{text-align:right}

  .method-row{display:flex;gap:24px;align-items:center;margin:10px 0 4px;font-size:12px}
  .method-row label{display:flex;align-items:center;gap:5px}
  .issue-row{font-size:12px;margin-bottom:10px}

  .cert-text{font-size:11.5px;color:#333;line-height:1.8;margin-top:14px;padding-top:10px;border-top:1px solid #ddd}
  .sig-line{border-top:1px solid #000;margin:0 20px;padding-top:5px;text-align:center}
  .sig-pair{display:grid;grid-template-columns:1fr 1fr;gap:60px;margin-top:40px}
  .sig-box{text-align:center}
  .sig-box .role{font-size:11.5px;font-weight:600;color:#000;margin-top:4px}
  .sig-box .date-line{font-size:11px;color:#555;margin-top:3px}

  .print-btn{display:block;margin:20px 0 0 auto;padding:7px 22px;background:#000;color:#fff;border:none;border-radius:5px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit}
  @media print{.print-btn{display:none}body{padding:0}.wc-table th{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  @page{margin:14mm;size:A4}
`;

function templateWC(doc) {
  const c       = doc.customer || {};
  const wht     = doc.wht_rate || 0;
  const t       = calcTotals({ items: doc.items || [], wht_rate: wht, price_mode: doc.price_mode || 'exclusive' });
  const section = doc.income_section || '40_8';
  const method  = Number(doc.submit_method || 1);
  const issueN  = doc.issue_count || 1;
  const incDesc = doc.income_description || (INCOME_SECTIONS.find(s => s.key === section)?.desc || 'ค่าบริการ');

  // Income type checkboxes
  const incomeChecks = INCOME_SECTIONS.map(s => {
    const checked = s.key === section;
    return `<div class="income-item">
      <input type="checkbox" ${checked ? 'checked' : ''} disabled>
      <div><div class="item-label">${esc(s.label)}</div><div class="item-desc">${esc(s.desc)}</div></div>
    </div>`;
  }).join('');

  // Payment rows from items
  const itemRows = (doc.items || []).map(it => {
    const gross  = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
    const disc   = it.discount_type === 'percent'
      ? gross * (Number(it.discount_value) || 0) / 100
      : Number(it.discount_value) || 0;
    const net    = gross - Math.min(disc, gross);
    const wtAmt  = Math.round(net * (wht / 100) * 100) / 100;
    return `<tr>
      <td>${thDateShort(doc.issue_date)}</td>
      <td>${esc(it.description || incDesc)}</td>
      <td class="r">${fmt(net)}</td>
      <td class="r">${wht}%</td>
      <td class="r">${fmt(wtAmt)}</td>
    </tr>`;
  }).join('') || `<tr>
    <td>${thDateShort(doc.issue_date)}</td>
    <td>${esc(incDesc)}</td>
    <td class="r">${fmt(t.subtotal)}</td>
    <td class="r">${wht}%</td>
    <td class="r">${fmt(t.wht_amount)}</td>
  </tr>`;

  const methods = [
    { v: 1, l: '(1) หัก ณ ที่จ่าย' },
    { v: 2, l: '(2) ออกให้ตลอดไป' },
    { v: 3, l: '(3) ออกให้ครั้งเดียว' },
  ].map(m => `<label><input type="checkbox" ${m.v === method ? 'checked' : ''} disabled> ${m.l}</label>`).join('');

  return `<!doctype html><html lang="th"><head><meta charset="utf-8">
  <title>${esc(doc.doc_number)}</title><style>${WC_CSS}</style></head><body>

  <div class="wc-title">
    <div class="th">หนังสือรับรองการหักภาษี ณ ที่จ่าย</div>
    <div class="sub">ตามมาตรา 50 ทวิ แห่งประมวลรัษฎากร</div>
    <div class="en">Withholding Tax Certificate (Section 50 Bis of the Revenue Code)</div>
  </div>

  <div style="text-align:right;font-size:11.5px;margin-bottom:10px">
    เลขที่ <strong>${esc(doc.doc_number)}</strong> &nbsp;|&nbsp; วันที่ <strong>${thDateShort(doc.issue_date)}</strong>
  </div>

  <div class="parties">
    <div class="party-box">
      <div class="role">ผู้จ่ายเงิน (Payer)</div>
      <div class="name">${esc(COMPANY.name)}</div>
      <div class="sub">เลขประจำตัวผู้เสียภาษี: ${esc(COMPANY.tax_id)}<br>${esc(COMPANY.address)}<br>${COMPANY.phone ? `โทร. ${esc(COMPANY.phone)}` : ''}</div>
    </div>
    <div class="party-box">
      <div class="role">ผู้มีเงินได้ (Recipient)</div>
      <div class="name">${esc(c.name || '-')}</div>
      <div class="sub">
        ${c.tax_id ? `เลขประจำตัวผู้เสียภาษี: ${esc(c.tax_id)}<br>` : ''}
        ${c.address ? esc(c.address) + '<br>' : ''}
        ${c.phone ? `โทร. ${esc(c.phone)}` : ''}
      </div>
    </div>
  </div>

  <div class="section-lbl">ประเภทเงินได้พึงประเมินที่จ่าย</div>
  <div class="income-grid">${incomeChecks}</div>

  <table class="wc-table">
    <thead><tr>
      <th style="width:110px">วันเดือนปีที่จ่ายเงิน</th>
      <th>ประเภทเงินได้พึงประเมิน</th>
      <th class="r" style="width:120px">จำนวนเงินที่จ่าย (บาท)</th>
      <th class="r" style="width:70px">อัตราภาษีร้อยละ</th>
      <th class="r" style="width:120px">จำนวนเงินภาษีที่หักไว้ (บาท)</th>
    </tr></thead>
    <tbody>${itemRows}</tbody>
    <tfoot><tr>
      <td colspan="2" style="text-align:right">รวมทั้งสิ้น</td>
      <td class="r">${fmt(t.subtotal)}</td>
      <td class="r"></td>
      <td class="r">${fmt(t.wht_amount)}</td>
    </tr></tfoot>
  </table>

  <div class="method-row">
    <span style="font-weight:700">วิธีนำส่ง:</span>
    ${methods}
  </div>
  <div class="issue-row">ออกให้ครั้งที่ <strong>${issueN}</strong></div>

  ${doc.notes ? `<div style="font-size:11.5px;color:#333;margin:6px 0"><strong>หมายเหตุ:</strong> ${esc(doc.notes)}</div>` : ''}

  <div class="cert-text">
    ขอรับรองว่าข้อความดังกล่าวข้างต้นถูกต้องตามความเป็นจริงทุกประการ
  </div>

  <div class="sig-pair">
    <div class="sig-box">
      <div class="sig-line"></div>
      <div class="role">ลงชื่อผู้จ่ายเงิน / ผู้มีหน้าที่หักภาษี ณ ที่จ่าย</div>
      <div class="date-line">วันที่ ......./......./.........</div>
    </div>
    <div class="sig-box">
      <div class="sig-line"></div>
      <div class="role">ลงชื่อผู้รับเงิน / ผู้มีเงินได้</div>
      <div class="date-line">วันที่ ......./......./.........</div>
    </div>
  </div>

  <button class="print-btn" onclick="window.print()">พิมพ์ / บันทึก PDF</button>
  </body></html>`;
}

// ─── Dispatcher ───────────────────────────────────────────────────────────────
const TEMPLATES = { QT: templateQT, IV: templateIV, RC: templateRC, TI: templateTI, WT: templateWT, WC: templateWC };

// settings: { bank_accounts: [...] }
export function printDocument(doc, toast, mode = 'preview', settings = null) {
  const fn = TEMPLATES[doc.doc_type];
  if (!fn) { toast?.error?.('ไม่รู้จักประเภทเอกสาร'); return; }
  const w = window.open('', '_blank');
  if (!w) { toast?.error?.('เปิดหน้าต่างพิมพ์ไม่ได้ — อนุญาต popup ก่อน'); return; }
  const bankAccounts = settings?.bank_accounts ?? null;
  let html = fn(doc, bankAccounts);
  if (mode === 'pdf') {
    html = html.replace('</body>', '<script>window.onload=function(){window.focus();window.print();}<\/script></body>');
  }
  w.document.write(html);
  w.document.close();
}
